import React from 'react';
import styled, { css } from 'styled-components';

import { MyBookStatusEnum } from '~/api/account/myBooksApiTypes';
import { MiniatureSizesEnum } from '~/api/book/bookApiTypes';
import { Avatar } from '~/atomic/atom/Avatar/Avatar';
import { MyBooksStatusIcon } from '~/atomic/atom/MyBooksStatusIcon';
import { UserProfileInfo } from '~/atomic/page/user/UserProfileInfo';
import { TextBoldStyle, TextRegStyle } from '~/atomic/Typography';
import { reactionsList } from '~/feature/myBooks/reactions/reactionsList';
import { userSelector } from '~/feature/user/user.selector';
import { BookReactionRatingEnum } from '~/graphql/books/factory/book/BookFactoryTypes';
import { lessThan } from '~/lib/mediaQuery';
import { useAppSelector } from '~/store';

export const UserProfile = () => {
  const { currentUser } = useAppSelector(userSelector);

  return (
    <SCUserProfile>
      <SCUserHeader>
        <SCAvatar
          imageSize={80}
          avatarImages={currentUser?.avatarImages}
          avatarSize={MiniatureSizesEnum.Original}
          alt={currentUser?.name}
          userName={currentUser?.name}
        />
        <SCName>
          {currentUser.name}
        </SCName>
        <SCUserStatus>
          Читатель
        </SCUserStatus>
      </SCUserHeader>
      <SCUserInfoWrapper>
        <UserProfileInfo
          title="Библиотека"
          itemCss={SCProfileInfoItem}
          items={[
            {
              name: 'Читаю',
              count: currentUser?.counters?.mybooks?.readNow,
              icon: <SCReadNowIcon status={MyBookStatusEnum.readNow} />,
            },
            {
              name: 'Прочитано',
              count: currentUser?.counters?.mybooks?.read,
              icon: <SCReadIcon status={MyBookStatusEnum.read} />,
            },
            {
              name: 'Хочу прочитать',
              count: currentUser?.counters?.mybooks?.readLater,
              icon: <SCReadLaterIcon status={MyBookStatusEnum.readLater} />,
            },
          ]}
        />
        <UserProfileInfo
          title="Оценки книг"
          items={reactionsList
            .filter((reaction) => reaction.name !== BookReactionRatingEnum.NotRated)
            .map((reaction) => {
              if (reaction.name === BookReactionRatingEnum.WillDo) {
                return {
                  ...reaction,
                  label: 'Читать можно',
                };
              }

              if (reaction.name === BookReactionRatingEnum.SoSo) {
                return {
                  ...reaction,
                  label: 'Могло быть и\nлучше',
                };
              }

              if (reaction.name === BookReactionRatingEnum.Bad) {
                return {
                  ...reaction,
                  label: 'Книга не для меня',
                };
              }

              return reaction;
            })
            .map((reaction) => ({
              name: reaction.label,
              count: currentUser?.counters?.mybooks?.reactions?.[reaction.camelCaseName],
              icon: <img
                width={48}
                height={48}
                src={reaction.icon}
                alt={`реакция ${reaction.label}`}
              />,
            }))}
        />
      </SCUserInfoWrapper>
    </SCUserProfile>
  );
};

export const SCUserProfile = styled.div`
  max-width: 780px;
  margin: 0 auto;
  
  ${lessThan('sm')} {
    margin: 0 16px;
  }
`;

const SCUserHeader = styled.div`
  background: #FFFFFF;
  padding: 18px 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const SCAvatar = styled(Avatar)`
  margin-bottom: 12px;
`;

const SCName = styled.h1`
  ${TextBoldStyle};
  color: var(--black-color);
  margin-bottom: 4px;
`;

const SCUserStatus = styled.span`
  ${TextRegStyle};
  color: var(--gray-color);
`;

const SCUserInfoWrapper = styled.div`
  display: flex;
  
  > *:not(:last-child) {
    margin-right: 42px;
  }
  
  ${lessThan('sm')} {
    flex-direction: column;
    align-items: center;

    > *:not(:last-child) {
      margin-right: 0;
      margin-bottom: 20px;
    }
  }
`;

const SCReadNowIcon = styled(MyBooksStatusIcon)`
  font-size: 32px;
  color: var(--black-color);
`;

const SCReadLaterIcon = styled(MyBooksStatusIcon)`
  font-size: 30px;
  color: var(--black-color);
`;

const SCReadIcon = styled(MyBooksStatusIcon)`
  font-size: 30px;
  color: var(--black-color);
`;

const SCProfileInfoItem = css`
  > *:not(:last-child) {
    margin-right: 16px;
  }
`;
